<template>
  <picture
    v-observe-visibility="{
      callback: handleScrollIntoView,
      once: true,
    }"
    :class="
      bemBlockMod({
        loading: !loaded,
        loaded: loaded,
      })
    "
  >
    <img
      ref="img"
      :class="[
        bemElement('img', {
          placeholder: placeholder && !loaded,
          loading: !loaded,
          loaded: loaded,
        }),
        imageClasses,
      ]"
      :sizes="sizes"
      :src="shouldLoad ? resolvedSrc : placeholderSrc"
      :srcset="shouldLoad && !error ? srcset : ''"
      :data-placeholder="placeholderSrc"
      :data-src="shouldLoad ? '' : resolvedSrc"
      :data-srcset="shouldLoad ? '' : srcset"
      :alt="resolvedAlt"
      :loading="loading"
      itemprop="image"
      draggable="false"
      @error.prevent="handleImageError"
      @load.prevent="handleImageLoad"
      @click="$emit('image-click', $event)"
    />
  </picture>
</template>

<script>
import bemClassesMixin from '../mixins/bemClassesMixin';
import checkVisibilityMixin from '../mixins/checkVisibilityMixin';
import { generatePlaceholderDataUri } from '../../common/utils/image';

export default {
  name: 'BaseImage',

  mixins: [bemClassesMixin, checkVisibilityMixin],

  props: {
    /** Image URL shown as the <img> src (fallback when srcset has no match). */
    src: {
      type: String,
      required: false,
      default: null,
    },
    /** Alternative text for the image. */
    alt: {
      type: String,
      required: false,
      default: '',
    },
    // TODO(SF-17898) RETIRAR RETROCOMPATIBILIDAD — hacer SOLO cuando la épica de
    // carruseles (SF-17898) y Tarjeta Regalo (SF-17297) estén AMBAS en master (si
    // solo una está en master, NO tocar: se rompería la otra).
    //
    // La prop `image` (objeto { url, alt }) y la aceptación de `placeholder` como
    // objeto son LEGACY. La API buena es plana: :src (String), :alt (String),
    // :placeholder (String url).
    //
    // Cómo retirarla (pasos para un agente):
    // 1. Buscar los callers que aún usan la API vieja:
    //      grep -rn ':image=' app_byl_core/cartridges/app_byl_core/cartridge/client/default/js/vue/components
    //      grep -rn ':placeholder=' ...components  (los que pasan un objeto, p. ej. previewPlaceholder)
    //    Callers conocidos hoy (migrar estos y los que aparezcan en el grep):
    //      - TheProductGiftCard.vue:
    //          :image="previewImage"             -> :src="previewImage.url"
    //          :placeholder="previewPlaceholder" -> :placeholder="previewPlaceholder.url"
    //      - TheProductGiftCardOptionSelector.vue:
    //          :image="getImageByOption(option)" -> :src="getImageByOption(option).url"
    // 2. Cuando NO quede ningún `:image=` ni placeholder-objeto en el repo, borrar de
    //    ESTE componente: la prop `image`, el `?? this.image?.url` de resolvedSrc, el
    //    `this.placeholder?.url ??` de placeholderSrc, el `this.image?.alt` de
    //    resolvedAlt, el watch `image()`, y el `Object` del type de `placeholder`
    //    (dejar solo String). Queda la API plana limpia de SF-18033.
    /** @deprecated Legacy ProductImage object { url, alt }. Use :src / :alt. */
    image: {
      type: Object,
      required: false,
      default: null,
    },
    imageClasses: {
      type: [String, Array],
      required: false,
      default: () => [],
    },
    /**
     * Same spec as HTML specification:
     * https://html.spec.whatwg.org/multipage/urls-and-fetching.html#lazy-loading-attributes
     *
     * However, default value will be 'lazy'
     */
    loading: {
      type: String,
      required: false,
      default: 'lazy',
      validator: (prop) => ['eager', 'lazy'].includes(prop),
    },
    /** Placeholder image shown while the image lazy-loads. String url (new API)
     * or legacy { url } object (see TODO on `image`). */
    placeholder: {
      type: [String, Object],
      required: false,
      default: null,
    },
    sizes: {
      type: String,
      required: false,
      default: null,
    },
    srcset: {
      type: String,
      required: false,
      default: null,
    },
    useTitle: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  data: () => ({
    /** @type {boolean} */
    error: false,
    /** @type {boolean} True if lazy image has finally been loaded */
    loaded: false,
    /** @type {boolean} True if lazy image has to be loaded because of becoming
     * visible on screen */
    shouldLoad: false,
  }),

  computed: {
    /** @type {string} Url to placeholder image. If the prop is empty, then the
     * data URI of the SVG is used */
    placeholderSrc() {
      return (
        this.placeholder?.url ?? this.placeholder ?? this.PLACEHOLDER_DATA_URI
      );
    },
    /** @type {string} Url shown as the img src, falling back to the placeholder
     * data URI when there is no src or the image failed to load */
    resolvedSrc() {
      const candidate = this.src ?? this.image?.url;
      if (this.error || !candidate) return this.PLACEHOLDER_DATA_URI;
      return candidate;
    },
    /** @type {string} Alt text, falling back to the legacy image.alt */
    resolvedAlt() {
      return this.alt || this.image?.alt || '';
    },
  },

  watch: {
    src() {
      this.reset();
    },
    image() {
      this.reset();
    },
    loading() {
      if (!this.loaded) {
        // Force recalculate shouldLoad and loading
        // But only when the image has not been loaded yet
        // Ex:
        //  - from "eager" to "lazy"
        //  - from visible "lazy" (and loaded) to "eager"
        this.reset();
      }
    },
  },

  created() {
    this.PLACEHOLDER_DATA_URI = generatePlaceholderDataUri(2, 3);
    this.reset();
  },

  methods: {
    handleImageError() {
      this.error = true;
    },
    handleImageLoad(event) {
      if (this.shouldLoad) {
        this.loaded = true;
        this.$emit('load', event);
      }
    },
    handleScrollIntoView(isVisible) {
      this.handleVisibilityChanged(isVisible);
      if (isVisible) {
        this.shouldLoad = true;
      }
    },
    reset() {
      this.loaded = false;
      this.shouldLoad = this.loading === 'eager';
      this.checkVisibility(this.handleScrollIntoView);
      if (this.$refs.img?.complete) {
        this.handleImageLoad();
      }
    },
  },
};
</script>
